/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: VendorsPage.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Provisioning Vendors module.
----------------------------------------------------------------*/

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Globe, Factory } from 'lucide-react';
import { useNotifications } from '../../contexts/NotificationContext';
import { useI18n } from '../../hooks/useI18n';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import * as api from './api';

export default function VendorsPage() {
    const navigate = useNavigate();
    const { t } = useI18n();
    const { showSuccess, showError } = useNotifications();

    // Data State
    const [allVendors, setAllVendors] = useState<api.Vendor[]>([]);
    const [loading, setLoading] = useState(true);

    // Grid State
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sortBy, setSortBy] = useState<string>('name');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
    const [visibleColumns, setVisibleColumns] = useState<Set<string>>(new Set(['logo', 'name', 'website_url', 'models_count', 'actions']));

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const data = await api.getVendors();
            setAllVendors(data);
        } catch (error) {
            console.error('Failed to load vendors:', error);
            showError(error instanceof Error ? error.message : 'Failed to load vendors');
        } finally {
            setLoading(false);
        }
    }, [showError]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // Derived Data for Grid (Client-side pagination/filtering)
    const processedData = useMemo(() => {
        let filtered = [...allVendors];

        // 1. Search
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(v =>
                v.name.toLowerCase().includes(query) ||
                v.websiteUrl?.toLowerCase().includes(query)
            );
        }

        // 2. Sort
        filtered.sort((a, b) => {
            let aValue: unknown = a[sortBy as keyof api.Vendor];
            let bValue: unknown = b[sortBy as keyof api.Vendor];

            if (sortBy === 'models_count') {
                aValue = a._count?.models || 0;
                bValue = b._count?.models || 0;
            }

            if (typeof aValue === 'string') aValue = aValue.toLowerCase();
            if (typeof bValue === 'string') bValue = bValue.toLowerCase();

            if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });

        // 3. Pagination
        const totalItems = filtered.length;
        const totalPages = Math.ceil(totalItems / itemsPerPage);
        const startIndex = (currentPage - 1) * itemsPerPage;
        const paginatedData = filtered.slice(startIndex, startIndex + itemsPerPage);

        return { data: paginatedData, totalItems, totalPages };
    }, [allVendors, searchQuery, sortBy, sortOrder, currentPage, itemsPerPage]);

    // Columns Definition
    const columns = useMemo(() => [
        {
            key: 'logo',
            label: 'Logo',
            sortable: false,
            visible: true,
            render: (_: unknown, vendor: api.Vendor) => (
                <div className="flex items-center justify-center">
                    {vendor.logo ? (
                        <img
                            src={`/uploads/vendors/${vendor.logo.split('/').pop()}`}
                            alt={vendor.name}
                            className="h-8 w-8 object-contain"
                            onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                                (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                            }}
                        />
                    ) : null}
                    <div className={`h-8 w-8 bg-gray-100 dark:bg-gray-700 rounded flex items-center justify-center text-gray-400 font-bold text-xs ${vendor.logo ? 'hidden' : ''}`}>
                        {vendor.name.substring(0, 2).toUpperCase()}
                    </div>
                </div>
            )
        },
        {
            key: 'name',
            label: 'Name',
            sortable: true,
            visible: true,
            render: (value: string) => <span className="font-medium text-gray-900 dark:text-gray-100">{value}</span>
        },
        {
            key: 'websiteUrl',
            label: 'Website',
            sortable: true,
            visible: true,
            render: (value: string) => value ? (
                <a href={value} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-600 hover:underline text-sm">
                    <Globe size={14} /> {value.replace(/^https?:\/\//, '')}
                </a>
            ) : '-'
        },
        {
            key: 'models_count',
            label: 'Models',
            sortable: true,
            visible: true,
            render: (_: unknown, vendor: api.Vendor) => (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                    {vendor._count?.models || 0} Models
                </span>
            )
        },
        {
            key: 'actions',
            label: t('common.actions'),
            visible: true,
            sortable: false
        }
    ], [t]);

    const renderActions = useCallback((vendor: api.Vendor) => (
        <div className="flex items-center justify-end gap-2">
            <SimpleTooltip content={t('common.edit')} position="top">
                <button
                    onClick={() => navigate(`${vendor.uuid ?? vendor.id}`)}
                    className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors text-blue-600 dark:text-blue-400"
                >
                    <Edit className="h-4 w-4" />
                </button>
            </SimpleTooltip>
        </div>
    ), [navigate, t]);

    return (
        <div className="p-6">
            <DataGridSimple
                data={processedData.data}
                loading={loading}
                columns={columns}
                pagination={{
                    page: currentPage,
                    totalPages: processedData.totalPages,
                    total: processedData.totalItems,
                    limit: itemsPerPage,
                }}
                translations={{
                    showing: t('common.showing', 'Showing'),
                    to: t('common.to', 'to'),
                    of: t('common.of', 'of'),
                    results: t('common.results', 'results'),
                    items: t('common.items', 'Items'),
                    columns: t('common.columns', 'Columns'),
                    noItemsFound: t('common.noData', 'No items found'),
                    allSelected: t('common.allSelected', 'All Selected'),
                    selected: t('common.selected', 'Selected'),
                    searchOptions: t('common.search', 'Search'),
                    noOptionsFound: t('common.noResults', 'No results'),
                    selectAll: t('common.selectAll', 'Select All'),
                    clearAll: t('common.clearAll', 'Clear All'),
                    previous: t('common.previous', 'Previous'),
                    next: t('common.next', 'Next'),
                    page: t('common.page', 'Page'),
                    deleteItem: t('common.deleteItem', 'Delete Item'),
                    areYouSureDeleteItem: t('common.confirmDelete', 'Are you sure you want to delete this item?'),
                    delete: t('common.delete', 'Delete'),
                    cancel: t('common.cancel', 'Cancel'),
                }}
                searchQuery={searchQuery}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSearch={setSearchQuery}
                onSort={(column) => {
                    if (sortBy === column) {
                        setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
                    } else {
                        setSortBy(column);
                        setSortOrder('asc');
                    }
                }}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={setItemsPerPage}
                onColumnToggle={(column) => {
                    const newSet = new Set(visibleColumns);
                    if (newSet.has(column)) newSet.delete(column);
                    else newSet.add(column);
                    setVisibleColumns(newSet);
                }}
                renderActions={renderActions}
                searchPlaceholder="Search vendors..."
                title="Device Vendors"
                titleIcon={<Factory className="h-5 w-5 text-primary" />}
                addButtonText={t('common.add', 'Add')}
                onAdd={() => navigate('new')}
            />
        </div>
    );
}
